import { useState, useRef, useEffect } from 'react';
import { User, Settings, LogOut, ChevronDown, Crown, Shield } from 'lucide-react';
import { AccountDataModal } from './AccountDataModal';
import { AccountSettingsModal } from './AccountSettingsModal';

export const UserAvatar = ({ user, onLogout }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAccountData, setShowAccountData] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const dropdownRef = useRef(null);

  // Get first letter of name
  const getInitial = () => {
    if (!user?.name) return 'U';
    return user.name.charAt(0).toUpperCase();
  };

  // Get plan display name
  const getPlanName = () => {
    const planType = user?.plan_type || 'free';
    switch (planType) {
      case 'basic':
        return 'Basic – 4.99 €/lună';
      case 'pro':
        return 'Pro – 9.99 €/lună';
      case 'premium':
        return 'Premium – 29.99 €/lună';
      default:
        return 'Free';
    }
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleOpenAccountData = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    // Small delay to ensure dropdown closes before modal opens
    setTimeout(() => {
      setShowAccountData(true);
    }, 50);
  };

  const handleOpenAccountSettings = (e) => {
    e.stopPropagation();
    setIsDropdownOpen(false);
    // Small delay to ensure dropdown closes before modal opens
    setTimeout(() => {
      setShowAccountSettings(true);
    }, 50);
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Avatar Button */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 group"
          data-testid="user-avatar-btn"
          aria-expanded={isDropdownOpen}
          aria-haspopup="true"
        >
          {/* Avatar Circle */}
          <div 
            className="w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
            style={{
              background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: isDropdownOpen ? '0 0 12px rgba(212, 175, 55, 0.6)' : 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 12px rgba(212, 175, 55, 0.6)';
            }}
            onMouseLeave={(e) => {
              if (!isDropdownOpen) {
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            {user?.profile_photo ? (
              <img 
                src={user.profile_photo} 
                alt={user.name}
                className="w-full h-full rounded-full object-cover"
              />
            ) : (
              <span 
                className="text-white font-bold text-xl select-none"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {getInitial()}
              </span>
            )}
          </div>
          
          {/* Dropdown Indicator */}
          <ChevronDown 
            className={`w-4 h-4 text-white/60 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {/* Dropdown Menu */}
        {isDropdownOpen && (
          <div 
            className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-2xl overflow-hidden z-50 animate-fade-in"
            style={{
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            }}
            data-testid="user-dropdown-menu"
          >
            {/* User Info Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-navy to-navy/90 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                  }}
                >
                  <span className="text-white font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {getInitial()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-white font-semibold text-sm truncate">{user?.name || 'Utilizator'}</p>
                  <p className="text-white/60 text-xs truncate">{user?.email}</p>
                </div>
              </div>
              
              {/* Plan Badge */}
              <div className="mt-2 flex items-center gap-1">
                {user?.plan_type === 'pro' || user?.plan_type === 'premium' ? (
                  <Crown className="w-3 h-3 text-gold" />
                ) : (
                  <Shield className="w-3 h-3 text-white/40" />
                )}
                <span className="text-xs text-white/70">{getPlanName()}</span>
              </div>
            </div>

            {/* Menu Items */}
            <div className="p-3 space-y-2">
              {/* Datele Contului Button */}
              <button
                onClick={handleOpenAccountData}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left"
                style={{
                  background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                  color: 'white',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                data-testid="account-data-btn"
              >
                <User className="w-5 h-5" />
                <span className="font-semibold">Datele Contului</span>
              </button>

              {/* Setările Contului Button */}
              <button
                onClick={handleOpenAccountSettings}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-left bg-white border-2"
                style={{
                  borderColor: '#0A2540',
                  color: '#0A2540',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(10, 37, 64, 0.05)';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                data-testid="account-settings-btn"
              >
                <Settings className="w-5 h-5" />
                <span className="font-semibold">Setările Contului</span>
              </button>
            </div>

            {/* Quick Logout */}
            <div className="px-3 pb-3">
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  onLogout();
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-all text-sm font-medium"
                data-testid="quick-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Deconectare rapidă
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <AccountDataModal 
        isOpen={showAccountData}
        onClose={() => setShowAccountData(false)}
        user={user}
      />
      
      <AccountSettingsModal 
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        onLogout={onLogout}
      />
    </>
  );
};
